import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  mailboxService,
  MailboxFilters,
  CreateMailboxRequest,
  UpdateMailboxRequest,
  TestConnectionRequest,
} from '@services/mailbox.service'

export function useMailboxes(filters: MailboxFilters = {}) {
  return useQuery({
    queryKey: ['mailboxes', filters],
    queryFn: () => mailboxService.fetchMailboxes(filters),
  })
}

export function useMailbox(id: number) {
  return useQuery({
    queryKey: ['mailbox', id],
    queryFn: () => mailboxService.getMailbox(id),
    enabled: !!id,
  })
}

export function useMailboxStats() {
  return useQuery({
    queryKey: ['mailboxes', 'stats'],
    queryFn: mailboxService.getStats,
    refetchInterval: 60000, // Refresh every minute
  })
}

export function useCreateMailbox() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateMailboxRequest) => mailboxService.createMailbox(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['mailboxes', 'stats'] })
    },
  })
}

export function useUpdateMailbox(id: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateMailboxRequest) => mailboxService.updateMailbox(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['mailbox', id] })
      queryClient.invalidateQueries({ queryKey: ['mailboxes', 'stats'] })
    },
  })
}

export function useDeleteMailbox() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => mailboxService.deleteMailbox(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['mailboxes', 'stats'] })
    },
  })
}

export function useTestConnection() {
  return useMutation({
    mutationFn: (data: TestConnectionRequest) => mailboxService.testConnection(data),
  })
}

export function useResetDailyCount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => mailboxService.resetDailyCount(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      queryClient.invalidateQueries({ queryKey: ['mailbox', id] })
      queryClient.invalidateQueries({ queryKey: ['mailboxes', 'stats'] })
    },
  })
}
